import React from 'react';
import PropTypes from 'prop-types';
import debug from 'debug';
import Helmet from 'react-helmet';
import { withRouter } from 'react-router';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty(target, key, source[key]);
    });
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

const warn = debug('gtm:api:warning');
const log = debug('gtm:api:log');
class Api {
  constructor() {
    _defineProperty(this, "dataLayerName", void 0);

    _defineProperty(this, "gtm", void 0);

    _defineProperty(this, "queue", []);

    _defineProperty(this, "dataLayer", {});

    _defineProperty(this, "init", dataLayerName => {
      this.dataLayerName = dataLayerName;
    });

    _defineProperty(this, "loaded", (gtm = null) => {
      this.gtm = gtm || window[this.dataLayerName];

      if (this.queue.length > 0) {
        this.queue.forEach(this.trigger);
      }
    });

    _defineProperty(this, "setDataLayer", data => {
      this.dataLayer = _objectSpread({}, this.dataLayer, data);
    });

    _defineProperty(this, "trigger", data => {
      if (this.gtm) {
        const eventData = _objectSpread({}, this.dataLayer, data);

        this.gtm.push(eventData);
        log('Event triggered', _objectSpread({}, eventData));
      } else {
        warn('"gtm" not loaded! Event added to the queue');
        this.queue.push(data);
      }
    });
  }

}

var GtmContext = React.createContext({
  api: null
});

const canUseDom = () => typeof window !== 'undefined';

const loadScript = ({
  id,
  auth,
  preview
}, dataLayerName, loadCallback) => {
  const script = document.createElement('SCRIPT');
  let src = `https://www.googletagmanager.com/gtm.js?id=${id}`;

  if (auth) {
    src += `&gtm_auth=${auth}`;
  }

  if (preview) {
    src += `&gtm_preview=${preview}`;
  }

  script.async = true;
  script.src = src;

  script.onload = () => {
    window[dataLayerName].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });
    loadCallback();
  };

  script.onerror = () => {
    throw new Error('Google Tag Manager could not be loaded.');
  };

  document.head.appendChild(script);
};
const loadNoScript = gtmID => {
  document.body.appendChild(`<iframe src="//www.googletagmanager.com/ns.html?id=${gtmID}"
        height="0" width="0" style="display:none;visibility:hidden" id="tag-manager"></iframe>`);
};
var loadScripts = ((gtm, dataLayerName, loadCallback) => {
  // Check if we can use the dom just to be sure
  if (canUseDom()) {
    loadScript(gtm, dataLayerName, loadCallback);
  }
});

var scripts = /*#__PURE__*/Object.freeze({
  loadScript: loadScript,
  loadNoScript: loadNoScript,
  default: loadScripts
});

const {
  shape,
  func
} = PropTypes;
const GTMShape = shape({
  api: shape({
    trigger: func.isRequired,
    setDataLayer: func.isRequired
  }).isRequired
});

var withGTM = (Component => React.forwardRef((props, ref) => React.createElement(GtmContext.Consumer, null, GTM => React.createElement(Component, _extends({}, props, {
  GTM: GTM,
  ref: ref
})))));

class PageView extends React.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "sendPageView", false);

    _defineProperty(this, "triggerPageView", () => {
      if (!this.sendPageView) {
        return;
      }

      this.sendPageView = false;
      const {
        data: eventData,
        event,
        GTM,
        location,
        settings
      } = this.props;

      let data = _objectSpread({}, eventData, {
        [settings.sendAs]: location[settings.locationProp]
      });

      if (event && !data.event) {
        data.event = event;
      }

      GTM.api.trigger(data);
    });
  }

  componentDidMount() {
    this.sendPageView = true;
  }

  componentDidUpdate(prevProps) {
    const {
      location: {
        pathname: newPathName
      }
    } = this.props;
    const {
      location: {
        pathname: oldPathName
      }
    } = prevProps;

    if (newPathName !== oldPathName) {
      this.sendPageView = true;
    }
  }

  render() {
    /**
     * We use Helmet's 'onChangeClientState' so we are sure the dom data is updated before sending the PageView
     */
    return React.createElement(Helmet, {
      onChangeClientState: () => this.triggerPageView()
    });
  }

}

_defineProperty(PageView, "propTypes", {
  event: PropTypes.string,
  data: PropTypes.object,
  location: PropTypes.object.isRequired,
  settings: PropTypes.shape({
    locationProp: PropTypes.string,
    sendAs: PropTypes.string
  }),
  GTM: GTMShape.isRequired
});

_defineProperty(PageView, "defaultProps", {
  event: 'pageview',
  data: {},
  settings: {
    locationProp: 'pathname',
    sendAs: 'url'
  }
});

PageView.displayName = "PageView";
var PageView$1 = withRouter(withGTM(PageView));

class GTM extends React.Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "api", void 0);

    _defineProperty(this, "getValue", () => ({
      api: this.api
    }));

    this.api = new Api();
  }

  componentDidMount() {
    const {
      gtm,
      dataLayerName,
      dataLayer
    } = this.props;

    if (gtm && gtm.id) {
      this.api.init(dataLayerName);
      this.api.setDataLayer(dataLayer);
      loadScripts(gtm, dataLayerName, () => {
        this.api.loaded();
      });
    }
  }

  render() {
    const {
      children,
      settings: {
        sendPageView,
        pageView
      }
    } = this.props;
    return React.createElement(GtmContext.Provider, {
      value: this.getValue()
    }, React.createElement(React.Fragment, null, sendPageView && React.createElement(PageView$1, pageView), children));
  }

}

_defineProperty(GTM, "propTypes", {
  dataLayerName: PropTypes.string,
  dataLayer: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  gtm: PropTypes.shape({
    id: PropTypes.string,
    auth: PropTypes.string,
    preview: PropTypes.string
  }),
  settings: PropTypes.shape({
    sendPageView: PropTypes.bool,
    pageView: PropTypes.object
  })
});

_defineProperty(GTM, "defaultProps", {
  dataLayerName: 'dataLayer',
  dataLayer: {},
  gtm: {
    id: null,
    auth: null,
    preview: null
  },
  settings: {
    sendPageView: false,
    pageview: null
  }
});

GTM.displayName = "GTM";

class DataLayer extends React.Component {
  constructor(_props) {
    super(_props);

    _defineProperty(this, "updateDataLayer", () => {
      const {
        GTM
      } = props,
            data = _objectWithoutProperties(props, ["GTM"]);

      GTM.api.setDataLayer(data);
    });

    this.updateDataLayer();
  }

  componentDidUpdate() {
    this.updateDataLayer();
  }

  render() {
    const _this$props = this.props,
          {
      GTM,
      settings,
      children,
      settings: {
        passProps,
        withGTM
      }
    } = _this$props,
          rest = _objectWithoutProperties(_this$props, ["GTM", "settings", "children", "settings"]);

    if (!passProps) {
      return children;
    }

    let props = _objectSpread({}, rest);

    if (withGTM) props.GTM = GTM;
    return React.createElement(React.Fragment, null, children && React.Children.map(children, child => React.cloneElement(child, props)));
  }

}

_defineProperty(DataLayer, "propTypes", {
  GTM: PropTypes.shape({
    api: PropTypes.shape({
      setDataLayer: PropTypes.func.isRequired
    }).isRequired
  }).isRequired,
  settings: PropTypes.shape({
    passProps: PropTypes.bool,
    withGTM: PropTypes.bool
  }),
  children: PropTypes.oneOf([PropTypes.element, PropTypes.arrayOf(PropTypes.element)])
});

_defineProperty(DataLayer, "defaultProps", {
  settings: {
    passProps: false,
    withGTM: false
  },
  children: null
});

DataLayer.displayName = "DataLayer";
var DataLayer$1 = withGTM(DataLayer);

class Click extends React.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "handleOnClick", e => {
      const {
        data,
        event,
        onClick,
        GTM
      } = this.props;

      if (event && !data.event) {
        data.event = event;
      }

      GTM.api.trigger(data);

      if (onClick) {
        onClick(e);
      }
    });
  }

  render() {
    const _this$props = this.props,
          {
      children,
      onClick,
      event
    } = _this$props,
          props = _objectWithoutProperties(_this$props, ["children", "onClick", "event"]);

    return React.createElement(React.Fragment, null, React.Children.map(children, child => React.cloneElement(child, _objectSpread({
      onClick: this.handleOnClick
    }, props))));
  }

}

_defineProperty(Click, "propTypes", {
  onClick: PropTypes.func,
  event: PropTypes.string,
  data: PropTypes.object,
  GTM: GTMShape.isRequired,
  children: PropTypes.element.isRequired
});

_defineProperty(Click, "defaultProps", {
  event: null,
  data: {},
  onClick: null
});

Click.displayName = "Click";
var Click$1 = withGTM(Click);

export default GTM;
export { Click$1 as Click, DataLayer$1 as DataLayer, GTMShape, PageView$1 as PageView, scripts, withGTM };
